import { useState } from 'react'
import toast from 'react-hot-toast'
import { harnessInstallSummary, SKILL_HARNESSES } from './harnessMeta'

interface AgentHarnessAutoInstallProps {
  projectName: string
  defaultHarness?: string
  /** Controlled harness value (when the parent owns the harness selection). */
  harness?: string
  /** Called when the user picks a different harness in controlled mode. */
  onHarnessChange?: (harness: string) => void
}

/**
 * First-choice install step for the Project Setup page: instead of
 * generate → copy → paste by hand, the user pastes one prompt into their
 * agent, which calls the `setup_agent_harness` MCP tool, writes the
 * harness-specific file set itself, then chains `setup_code_graph` +
 * `get_code_brief` to index the repo. The manual steps below remain as the
 * fallback.
 */
export default function AgentHarnessAutoInstall({
  projectName,
  defaultHarness = 'cursor',
  harness: controlledHarness,
  onHarnessChange,
}: AgentHarnessAutoInstallProps) {
  const [internalHarness, setInternalHarness] = useState(defaultHarness)
  const harness = controlledHarness ?? internalHarness
  const setHarness = onHarnessChange ?? setInternalHarness
  const [copied, setCopied] = useState(false)

  const prompt =
    `Call the setup_agent_harness MCP tool with project_name="${projectName || '<project>'}" ` +
    `and harness="${harness}", then write each returned file to its path ` +
    `(skill file: overwrite; AGENTS.md/CLAUDE.md: merge between the managed markers; ` +
    `settings file: merge alongside existing settings). ` +
    `Then index this repo for code-aware answers: detect the repo checkout directory, ` +
    `ask me which branch to index (pass it as CH_BRANCH when non-interactive), ` +
    `call the setup_code_graph MCP tool with project_name="${projectName || '<project>'}", ` +
    `write the returned scriptContent to the returned scriptPath (sh) and powershellScriptContent ` +
    `to powershellScriptPath (ps1), run the platform script ` +
    `(` +
    `sh ./ch-code-graph-setup.sh <repo-dir> on macOS/Linux/Git Bash, or ` +
    `powershell -ExecutionPolicy Bypass -File .\\ch-code-graph-setup.ps1 on Windows PowerShell` +
    `) (auth reuses your harness MCP key — ` +
    `CH_MCP_TOKEN env overrides), then call get_code_brief with ` +
    `project_name="${projectName || '<project>'}" until the index reports READY, ` +
    `then delete the temp files (the setup script file plus the .chcode bundle if one remains).`

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(prompt)
      setCopied(true)
      toast.success('Install prompt copied')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-harbor-text-secondary">
        Fastest path — no copy-paste of file contents. Paste the prompt below into your agent (it
        must have the <code className="text-emerald-400">context-harbor</code> MCP server
        configured). The agent pulls the {harnessInstallSummary(harness)} for this project from the
        server and writes the files into your repo itself.
      </p>
      {onHarnessChange === undefined && (
        <div>
          <label
            htmlFor="harness-select"
            className="mb-1 block text-sm font-medium text-harbor-text-secondary"
          >
            Editor / harness (picks the default skill-file location)
          </label>
          <select
            id="harness-select"
            value={harness}
            onChange={(e) => setHarness(e.target.value)}
            className="w-full rounded-lg border border-harbor-border bg-harbor-surface-raised px-3 py-2 text-harbor-text focus:border-transparent focus:outline-none focus:ring-2 focus:ring-emerald-500 md:w-64"
          >
            {SKILL_HARNESSES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="rounded-lg bg-harbor-surface-raised p-4">
        <pre className="whitespace-pre-wrap font-mono text-sm text-harbor-text-secondary">
          {prompt}
        </pre>
      </div>
      <button
        onClick={copyPrompt}
        disabled={!projectName}
        className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-harbor-text transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {copied ? 'Copied!' : 'Copy Install Prompt'}
      </button>
      <p className="text-sm text-harbor-text-secondary">
        No agent handy? Continue with the manual steps below — same files, more hops.
      </p>
    </div>
  )
}
